import { defineStore } from 'pinia'
import api from 'src/api'
import { Simulation } from 'src/models/Simulation'
import { SimulationSendDTO } from '@models/api/simulation/SimulationSendDTO'
import { MeteoConditions } from 'src/models/MeteoConditions'
import dayjs, { Dayjs } from 'dayjs'
import { SimulateStatusUpdateEvent } from '@models/simulation/SimulateStatusUpdateEvent'
import { SimulationStatus } from '@enums/SimulationStatus'
import { emitter } from '@services/emitter'
import { ref } from 'vue'
import { SimulationListItemDTO } from '@models/api/simulation/SimulationListItemDTO'
import { SimulationListItem } from '@models/simulation/SimulationListItem'
import { MeteoConditionsDTO } from '@models/api/MeteoConditionsDTO'
import pDebounce from 'p-debounce'
import { useSimulationFuelModelsStore } from './simulationFuelModelsStore'

export const useSimulationStore = defineStore('simulation', () => {
  // State
  const simulations = ref<SimulationListItem[]>([])
  const simulationsList = ref<SimulationListItem[]>([])
  const selectedSimulation = ref<Simulation | undefined | null>(null)
  const simulationCustomMeteoConditions = ref<MeteoConditions[]>()
  const simulationDefaultMeteoConditions = ref<MeteoConditions[]>()
  const savedDefaultMeteoConditions = ref<MeteoConditionsDTO[]>()
  const sliderTimeMinutes = ref(0)
  const isSimulationChartPanelOpen = ref(false)
  const sliderTime = ref<Dayjs | null>(null)
  const debouncedGetMeteoRequest = pDebounce(
    api.simulation.getSimulationMeteoModel,
    1000
  )

  async function fetchSimulations() {
    try {
      const response = await api.simulation.allSimulations()
      SET_SIMULATIONS(response.data)
    } catch (error) {
      console.error('The simulations failed on load', error)
    }
  }

  async function addSimulation(newSimulation: Simulation) {
    try {
      const result = await api.simulation.addSimulation(
        SimulationSendDTO.fromSimulation(newSimulation)
      )
      const simulationListItem = SimulationListItem.fromSimulationDTO(
        result.data
      )
      ADD_SIMULATION(simulationListItem)
      return simulationListItem
    } catch (error) {
      console.error('The simulation failed on add', error)
      throw error
    }
  }

  async function deleteSimulation(simulationId: string) {
    try {
      await api.simulation.removeSimulation(simulationId)
      REMOVE_SIMULATION(simulationId)
    } catch (error) {
      console.error('The simulation failed on remove', error)
      throw error
    }
  }

  async function fetchSimulationMeteoConditions({
    lat,
    lon,
    date,
    hoursDuration,
    updateCustomConditions = true
  }: {
    lat: number
    lon: number
    date: Dayjs
    hoursDuration: number
    updateCustomConditions?: boolean
  }) {
    try {
      const simulationModel = await debouncedGetMeteoRequest(
        lat,
        lon,
        hoursDuration,
        date
      )
      if (updateCustomConditions) {
        SET_SIMULATION_METEO_CONDITIONS(simulationModel)
      }
      SET_SIMULATION_METEO_DEFAULT_CONDITIONS(
        structuredClone(simulationModel.map((meteo) => meteo.toDTO()))
      )
      return simulationModel
    } catch (error) {
      SET_SIMULATION_METEO_CONDITIONS(undefined)
      SET_SIMULATION_METEO_DEFAULT_CONDITIONS(undefined)
      console.error(
        'The simulation failed on fetchSimulationCustomConditions',
        error
      )
      throw error
    }
  }

  function SET_SIMULATIONS(newValue: SimulationListItemDTO[]) {
    try {
      simulationsList.value = newValue
        .sort((a, b) => {
          return (
            new Date(b.CREATION_TIME).getTime() -
            new Date(a.CREATION_TIME).getTime()
          )
        })
        .map((simulation) => SimulationListItem.fromDTO(simulation))
    } catch (error) {
      console.error('The simulations failed on set', error)
    }
  }

  function UPDATE_SIMULATION_STATUS(event: SimulateStatusUpdateEvent) {
    const simulation = simulations.value.find(
      (simulation) =>
        simulation.id.toUpperCase() === event.id_simulation.toUpperCase()
    )
    if (simulation) {
      simulation.status = event.status
      if (simulation.status === SimulationStatus.POSTPROCESSING) {
        setTimeout(() => {
          emitter.emit('simulationComplete', simulation)
        }, 1000)
      }
      if (simulation.status === SimulationStatus.COMPLETED) {
        emitter.emit('simulationFileComplete', simulation)
      }
    }
  }

  function ADD_SIMULATION(newSimulation: SimulationListItem) {
    simulationsList.value.unshift(newSimulation)
    simulations.value.unshift(newSimulation)
  }

  function REMOVE_SIMULATION(simulationId: SimulationListItem['id']) {
    simulations.value = simulations.value.filter(
      (simulation) => simulation.id !== simulationId
    )
    simulationsList.value = simulationsList.value.filter(
      (simulation) => simulation.id !== simulationId
    )
  }

  function SET_SIMULATION_METEO_DEFAULT_CONDITIONS(
    newValue?: MeteoConditionsDTO[]
  ) {
    savedDefaultMeteoConditions.value = newValue
    if (!newValue) {
      simulationDefaultMeteoConditions.value = newValue
      return
    }
    simulationDefaultMeteoConditions.value = newValue.map((meteo) =>
      MeteoConditions.fromDTO(meteo)
    )
  }
  function SET_SIMULATION_METEO_CONDITIONS(newValue?: MeteoConditions[]) {
    simulationCustomMeteoConditions.value = newValue
    if (newValue) {
      useSimulationFuelModelsStore().calculateAndApplyDefaultHumidity()
    }
  }
  function SET_SELECTED_SIMULATION(newValue: Simulation | undefined | null) {
    selectedSimulation.value = newValue
  }
  function SET_SLIDER_TIME_MINUTES(newValue: number) {
    sliderTimeMinutes.value = newValue
    if (selectedSimulation.value) {
      sliderTime.value = dayjs(selectedSimulation.value.initDate).add(
        sliderTimeMinutes.value,
        'minute'
      )
    }
  }

  return {
    // State
    simulations,
    simulationsList,
    simulationCustomConditions: simulationCustomMeteoConditions,
    selectedSimulation,
    simulationDefaultConditions: simulationDefaultMeteoConditions,
    sliderTimeMinutes,
    sliderTime,
    isSimulationChartPanelOpen,

    // Getters (export here if needed)
    // someComputed,

    // Actions
    fetchSimulations,
    addSimulation,
    deleteSimulation,
    fetchSimulationCustomConditions: fetchSimulationMeteoConditions,
    SET_SIMULATIONS,
    UPDATE_SIMULATION_STATUS,
    ADD_SIMULATION,
    REMOVE_SIMULATION,
    SET_SIMULATION_DEFAULT_CONDITIONS: SET_SIMULATION_METEO_DEFAULT_CONDITIONS,
    SET_SIMULATION_CUSTOM_CONDITIONS: SET_SIMULATION_METEO_CONDITIONS,
    SET_SELECTED_SIMULATION,
    SET_SLIDER_TIME_MINUTES: SET_SLIDER_TIME_MINUTES
  }
})
